import CircularProgress from '@mui/material/CircularProgress'

import './style.scss'

import { notificationUtils } from 'features/notifications/index.ts'
import { type Orders, ordersApi } from 'features/orders'
import { api as subscriptionApi } from 'features/subscription/api.ts'
import { type Plan, usePlans } from 'features/subscription/hooks/usePlans.ts'
import { useState } from 'react'
import { notifyError } from 'shared/errors/notificate.ts'

import board from '../../assets/image/board.png'
import doneSvg from '../../assets/image/icons/svg/done.svg'
import ModalWindow from '../../components/molecules/ModalWindow'
import { HomeTemplate } from '../../components/templates/homeTemplate'
import { CONSTS } from '../../config/objectConst'
import { appSelectors } from '../../redux/reducers/app.ts'
import { useAppSelector } from '../../shared/hooks'
import { useCheckout } from './hooks/useCheckout.ts'
import { useLeftMenu } from './hooks/useLeftMenu.ts'

declare global {
	interface Window {
		fastSpringCheckoutClosed: any
	}
}

const INITIAL_MODAL: {
	id: string
	mode: 'downgrade' | 'upgrade'
} = {
	id: '',
	mode: 'downgrade'
}

export function UpgradePage() {
	const {
		data: subscription,
		isSuccess,
		...queryOptions
	} = subscriptionApi.useSubscriptionQuery()
	const [upgrade, mutationOptions] = subscriptionApi.useUpgradeMutation()
	const [syncOrders, ordersOptions] = ordersApi.useSyncMutation()
	const refreshAccessToken = useAppSelector(appSelectors.refreshAccessToken)

	const [modal, setModal] = useState(INITIAL_MODAL)

	useLeftMenu()
	const plans = usePlans()

	const openCheckout = useCheckout()

	window.fastSpringCheckoutClosed = fastSpringCheckoutClosed

	const changeSubscription = ({ id, price }: Plan) => {
		if (!isSuccess) {
			return
		}

		const shouldUpgrade = subscription.base_price < price

		setModal({
			id,
			mode: shouldUpgrade ? 'upgrade' : 'downgrade'
		})
	}

	async function fastSpringCheckoutClosed(data: Orders.Body) {
		if (!data) {
			return
		}

		try {
			await syncOrders(data).unwrap()
			await refreshAccessToken()
			notificationUtils.snackById('3.19')
		} catch (error) {
			notifyError(error)
		}
	}

	const upgradeSubscription = async (planId: string) => {
		setModal(INITIAL_MODAL)
		try {
			await upgrade(planId).unwrap()
			await refreshAccessToken()
			notificationUtils.snackById('n3.22')
		} catch (error) {
			notifyError(error)
		}
	}

	const isLoading =
		queryOptions.isLoading ||
		mutationOptions.isLoading ||
		ordersOptions.isLoading

	const isTrial =
		isSuccess && subscription.plan_id === CONSTS.SUBSCRIPTIONS.TRIAL.ID
	const isActive = isSuccess && subscription.is_active

	const modalText =
		CONSTS.confirmationDialogs[modal.mode === 'upgrade' ? '1.11' : '1.12']
	return (
		<HomeTemplate>
			{isLoading ? (
				<div className='material-loader-wrapper'>
					<CircularProgress
						classes={{ colorPrimary: 'material-loader' }}
						color='primary'
						size={60}
					/>
				</div>
			) : (
				<div className='containerUpgrade'>
					<h2 className='containerUpgrade__main-title'>
						Choose subscription plan
					</h2>

					<div className='containerUpgrade__wrapper'>
						<img
							alt='board'
							className='containerUpgrade__team-img'
							src={board}
						/>
						{plans.map((plan, i) => (
							<div
								className={
									plan.active
										? 'containerUpgrade__individual-active'
										: 'containerUpgrade__individual'
								}
								key={i}
							>
								<h2 className='containerUpgrade__title-accent'>{plan.name}</h2>
								<p className='containerUpgrade__description'>
									{plan.description}
								</p>
								<div className='containerUpgrade__line' />
								<p className='containerUpgrade__price'>
									{plan.price}$ per month
								</p>
								{
									<>
										{plan.active ? (
											<button
												className={
													'containerUpgrade__button-subscription-active'
												}
											>
												Current plan
											</button>
										) : (
											<button
												className={'containerUpgrade__button-subscription'}
												onClick={() =>
													isTrial || !isActive
														? openCheckout(plan.path)
														: changeSubscription(plan)
												}
											>
												Upgrade subscription
											</button>
										)}
									</>
								}
								<p className='containerUpgrade__text'>Features overview:</p>
								<ul className='containerUpgrade__list'>
									{plan.arrayData.map(item => (
										<li className='containerUpgrade__item' key={item}>
											<img
												alt='done'
												className='containerUpgrade__img'
												src={doneSvg}
											/>
											{item}
										</li>
									))}
								</ul>
							</div>
						))}
					</div>
					<div className='containerUpgrade__container-link'>
						<a
							className='containerUpgrade__link'
							href='https://snaily.io/terms-and-conditions/'
							rel='noopener noreferrer'
							target='_blank'
						>
							Terms and Conditions
						</a>
					</div>
				</div>
			)}

			<ModalWindow
				closeHandler={() => setModal(INITIAL_MODAL)}
				closeText='No'
				confirmHandler={() => upgradeSubscription(modal.id)}
				confirmText='Yes'
				open={Boolean(modal.id)}
				{...modalText}
			/>
		</HomeTemplate>
	)
}
